import * as React from "react";
import { ProjectTabProps } from "../Projects";
import { withTranslation, WithTranslation, useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Button, DatePicker, Form, Checkbox, FormInstance, DatePickerProps } from "antd";
import { dayjs } from "Config/dayjs";

import { CheckboxChangeEvent } from "antd/lib/checkbox";

const range = (start: number, end: number) => {
  const result = [];
  for (let i = start; i < end; i++) {
    result.push(i);
  }
  return result;
};

const getDisabledTime: DatePickerProps["disabledTime"] = (date) => {
  let disabledHours = () => [];
  // eslint-disable-next-line unused-imports/no-unused-vars
  let disabledMinutes = (hour: number) => [];
  if (date) {
    const dayjsDate = dayjs(date as dayjs.Dayjs);
    if (dayjsDate.isSame(new Date(), "day")) {
      const now = dayjs();
      const currentHour = now.hour();
      const currentMinute = now.minute();

      disabledHours = () => range(0, currentHour);
      disabledMinutes = (hour) => {
        if (hour === currentHour) {
          return range(0, currentMinute + 5);
        }
        if (hour === currentHour + 1 && currentMinute > 55) {
          const diff = 59 - currentMinute;
          return range(0, 5 - diff);
        }
        return [];
      };
    }
  }
  return {
    disabledHours,
    disabledMinutes,
  };
};

interface TimerSelectPickerProps {
  value?: API_DeleteProject;
  projectStatus: API_DeleteProject["projectStatus"];
  onChange?: (val: API_DeleteProject) => void;
  isEditable: boolean;
}

const TimerSelectPicker = ({ value: scheduledJob, onChange, projectStatus, isEditable }: TimerSelectPickerProps) => {
  const [showPicker, setShowPicker] = React.useState(false);
  const { t } = useTranslation(I18N_NS.ADMIN);

  React.useEffect(() => {
    if (scheduledJob && scheduledJob.time && scheduledJob.time !== -1) {
      setShowPicker(true);
    }
  }, [scheduledJob]);

  const isValidTime = (job: API_DeleteProject) => job && job.time && job.time !== -1;

  const handleChange = (date: dayjs.Dayjs) => {
    // when reset timer
    if (!date) {
      setShowPicker(false);
      // send -1 to delete it on back-end
      if (scheduledJob) {
        onChange({ ...scheduledJob, time: -1 });
      } else {
        onChange(null);
      }
      // when edit timer
    } else {
      // edit current record if it is already created
      if (scheduledJob && scheduledJob.id) {
        onChange({ ...scheduledJob, time: +date.startOf("minute").format("x") });
        // or create new record
      } else {
        onChange({ projectStatus: projectStatus, time: +date.startOf("minute").format("x") });
      }
    }
  };

  const handleChangeNotify = (e: CheckboxChangeEvent) => {
    onChange({ ...scheduledJob, notify: e.target.checked });
  };

  if (!showPicker || !isEditable) {
    return (
      <Button onClick={() => setShowPicker(true)} disabled={!isEditable}>
        {t("Not set")}
      </Button>
    );
  }
  return (
    <>
      <DatePicker
        classNames={{ popup: { root: "hide-now" } }}
        onChange={handleChange}
        showTime={{ defaultValue: isValidTime(scheduledJob) ? dayjs(scheduledJob.time) : dayjs().add(5, "minutes") }}
        showNow={false}
        className="mr-5"
        defaultValue={isValidTime(scheduledJob) ? dayjs(scheduledJob.time) : undefined}
        disabledDate={(dayjsDate) => dayjsDate.isBefore(new Date(), "day")}
        disabledTime={getDisabledTime}
        format="DD.MM.YY HH:mm"
      />
      <Checkbox onChange={handleChangeNotify} className="mr-5" defaultChecked={scheduledJob?.notify} />
      {t("Show warning to customers on dashboard")}
    </>
  );
};

interface TimersTabProps extends ProjectTabProps, WithTranslation {
  status: API_ADMINPROJECT_STATUS;
  scheduledJobs: API_AdminProject["scheduledJobs"];
  formRef: React.RefObject<FormInstance>;
}

interface TimersTabState {
  archiveJobRecord: API_DeleteProject;
  deactivateJobRecord: API_DeleteProject;
}

export const TimersTab = withTranslation(I18N_NS.ADMIN)(
  class extends React.Component<TimersTabProps, TimersTabState> {
    constructor(props: TimersTabProps) {
      super(props);
      this.state = {
        archiveJobRecord: null,
        deactivateJobRecord: null,
      };
    }

    componentDidMount() {
      this.initTimerValues();
    }

    initTimerValues() {
      const values = this.props.formRef?.current?.getFieldValue("scheduledJobs");
      if (values) {
        const { archiveJobRecord = null, deactivateJobRecord = null } = values.reduce((obj, curr) => {
          if ((curr as API_DeleteProject).projectStatus === "ARCHIVE") {
            obj.archiveJobRecord = curr;
          } else if ((curr as API_DeleteProject).projectStatus === "DEACTIVATE") {
            obj.deactivateJobRecord = curr;
          }
          return obj;
        }, {});
        this.setState({ archiveJobRecord, deactivateJobRecord });
      }
    }

    processInitValue = () => {
      const { scheduledJobs } = this.props;
      if (scheduledJobs) {
        return scheduledJobs.reduce((obj, curr) => {
          if (curr) {
            if ((curr as API_DeleteProject).projectStatus === "ARCHIVE") {
              obj.archiveJobRecord = curr;
            } else if ((curr as API_DeleteProject).projectStatus === "DEACTIVATE") {
              obj.deactivateJobRecord = curr;
            }
          }
          return obj;
        }, {} as { [key: string]: API_DeleteProject });
      }
      return undefined;
    };

    handleFieldsChange = (changedField, allFields) => {
      this.props.formRef.current.setFieldsValue({
        scheduledJobs: allFields.map((item) => item.value).filter((item) => !!item),
      });
    };

    render() {
      const { t, status } = this.props;
      return (
        <Form layout="vertical" onFieldsChange={this.handleFieldsChange} initialValues={this.processInitValue()}>
          <Form.Item label={t('Automatically set status to "Archived" at')} name="archiveJobRecord">
            <TimerSelectPicker projectStatus="ARCHIVE" isEditable={status === "ENABLED"} />
          </Form.Item>
          <Form.Item label={t('Automatically set status to "Deactivated" at')} name="deactivateJobRecord">
            <TimerSelectPicker projectStatus="DEACTIVATE" isEditable={status !== "DEACTIVATED"} />
          </Form.Item>
        </Form>
      );
    }
  }
);
